import {useCallback, useEffect, useRef, useState} from "react";
import log from "loglevel";
import {
  AssignmentCandidate,
  EtaItemInput,
  ManifestFilter,
  SkillOverrideNoteInput,
  SuggestAssignmentStatus,
  SuggestAssignmentValidationError,
  useSuggestAssignJobMutation,
  useGetSuggestAssignmentResultLazyQuery,
  useSubmitSuggestAssignmentMutation,
  useCancelSuggestAssignmentMutation,
  useGetSuggestAssignmentUsageLazyQuery
} from "../generated/graphql";

type SuggestedAssignmentState =
  | "idle"
  | "submitting"
  | "polling"
  | "complete"
  | "failed"
  | "validationError"
  | "capReached";

const MAX_POLL_ATTEMPTS = 600; // 600 × 500ms = 5 minutes
const POLL_INTERVAL_MS = 500;
// Cross-component coupling: must match ERROR_MESSAGES.DAILY_CAP_REACHED in
// nxtgen-services/software/suggested-assignment/constants/index.ts exactly.
const DAILY_CAP_REACHED = "Daily suggestion limit reached";

export const useSuggestedAssignment = (enabled = true) => {
  const [state, setState] = useState<SuggestedAssignmentState>("idle");
  const [candidates, setCandidates] = useState<AssignmentCandidate[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<SuggestAssignmentValidationError[]>([]);
  const [isAssigning, setIsAssigning] = useState(false);
  const [usageRemaining, setUsageRemaining] = useState<number | null>(null);
  const [usageLimit, setUsageLimit] = useState<number | null>(null);
  const requestIdRef = useRef<string | null>(null);
  const jobIdRef = useRef<number>(0);
  const pollAttemptsRef = useRef<number>(0);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isPollingRef = useRef(false);

  const [submitMutation] = useSubmitSuggestAssignmentMutation();
  const [confirmMutation] = useSuggestAssignJobMutation();
  const [cancelMutation] = useCancelSuggestAssignmentMutation();
  const [fetchUsage] = useGetSuggestAssignmentUsageLazyQuery({fetchPolicy: "network-only"});

  const [fetchResult] = useGetSuggestAssignmentResultLazyQuery({
    fetchPolicy: "network-only"
  });

  const refreshUsage = useCallback(async () => {
    try {
      const {data} = await fetchUsage();
      const usage = data?.getSuggestAssignmentUsage;
      if (usage) {
        setUsageLimit(usage.limit);
        setUsageRemaining(Math.max(0, usage.limit - usage.used));
      }
    } catch (e) {
      log.warn("Failed to fetch suggestion usage", e);
    }
  }, [fetchUsage]);

  const updateUsageFromResponse = useCallback(
    (responseUsageCount: number | null | undefined, responseUsageLimit: number | null | undefined) => {
      if (responseUsageLimit != null && responseUsageCount != null) {
        setUsageLimit(responseUsageLimit);
        setUsageRemaining(Math.max(0, responseUsageLimit - responseUsageCount));
      }
    },
    []
  );

  const stopPolling = useCallback(() => {
    isPollingRef.current = false;
    if (pollTimerRef.current !== null) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const startPolling = useCallback(
    (requestId: string) => {
      isPollingRef.current = true;
      pollAttemptsRef.current = 0;

      const poll = async () => {
        if (!isPollingRef.current) return;

        pollAttemptsRef.current++;
        if (pollAttemptsRef.current >= MAX_POLL_ATTEMPTS) {
          isPollingRef.current = false;
          setError("Request timed out");
          setState("failed");
          return;
        }

        try {
          const result = await fetchResult({variables: {input: {requestId}}});
          if (!isPollingRef.current) return;

          const response = result.data?.getSuggestAssignmentResult;
          if (!response) {
            pollTimerRef.current = setTimeout(() => void poll(), POLL_INTERVAL_MS);
            return;
          }

          if (response.status === SuggestAssignmentStatus.Complete) {
            isPollingRef.current = false;
            if (response.result) {
              setCandidates(response.result.candidates);
              setState("complete");
              // Refetch usage after successful completion (count incremented server-side)
              void refreshUsage();
            } else if (response.errors && response.errors.length > 0) {
              setValidationErrors(response.errors);
              setState("validationError");
            } else {
              setCandidates([]);
              setState("complete");
            }
            return;
          }

          if (
            response.status === SuggestAssignmentStatus.Failed ||
            response.status === SuggestAssignmentStatus.Cancelled
          ) {
            isPollingRef.current = false;
            setError(response.error ?? "Request failed");
            setState("failed");
            return;
          }

          pollTimerRef.current = setTimeout(() => void poll(), POLL_INTERVAL_MS);
        } catch (e) {
          if (!isPollingRef.current) return;
          log.error("Poll error", e);
          isPollingRef.current = false;
          setError("Polling failed");
          setState("failed");
        }
      };

      poll();
    },
    [fetchResult, refreshUsage]
  );

  const submit = useCallback(
    async (jobId: number, manifestFilter?: ManifestFilter) => {
      stopPolling();
      setState("submitting");
      setCandidates([]);
      setError(null);
      setValidationErrors([]);
      jobIdRef.current = jobId;

      try {
        const {data} = await submitMutation({
          variables: {input: {jobId, manifestFilter}}
        });

        const response = data?.submitSuggestAssignment;
        if (!response) {
          setError("No response from server");
          setState("failed");
          return;
        }

        // Update usage from the submit response
        updateUsageFromResponse(response.usageCount, response.usageLimit);

        // DP-3194: daily cap reached
        if (response.error === DAILY_CAP_REACHED) {
          setError(DAILY_CAP_REACHED);
          setState("capReached");
          return;
        }

        if (response.status === SuggestAssignmentStatus.Complete && response.errors.length > 0) {
          setValidationErrors(response.errors);
          setState("validationError");
          return;
        }

        if (response.status === SuggestAssignmentStatus.Pending) {
          requestIdRef.current = response.requestId;
          setState("polling");
          startPolling(response.requestId);
          return;
        }

        setError(response.error ?? "Unexpected status");
        setState("failed");
      } catch (e) {
        log.error("submitSuggestAssignment error", e);
        setError("Failed to submit request");
        setState("failed");
      }
    },
    [submitMutation, startPolling, stopPolling, updateUsageFromResponse]
  );

  const confirmCandidate = useCallback(
    async (
      candidate: AssignmentCandidate,
      completedStopIds: number[],
      skillOverrideNotes?: SkillOverrideNoteInput[]
    ) => {
      if (jobIdRef.current <= 0) {
        log.error("confirmCandidate called without a valid jobId");
        return false;
      }
      setIsAssigning(true);
      try {
        const optimizedStopIds = (candidate.optimizedSequences ?? []).map((s) => s.jobStopId);
        const sequences = [...completedStopIds, ...optimizedStopIds];
        const optimizationEtas: EtaItemInput[] = (candidate.optimizedEtas ?? []).map((e) => ({
          jobStopId: e.jobStopId!,
          eta: e.eta!,
          etaSource: e.etaSource!,
          generatedDateTime: e.generatedDateTime!
        }));

        await confirmMutation({
          variables: {
            manifestDriverId: candidate.manifestDriverId,
            jobIds: [jobIdRef.current],
            sequences,
            optimizationEtas,
            ...(skillOverrideNotes?.length ? {skillOverrideNotes} : {})
          }
        });

        return true;
      } catch (e) {
        log.error("suggestAssignJob error", e);
        return false;
      } finally {
        setIsAssigning(false);
      }
    },
    [confirmMutation]
  );

  const cancel = useCallback(() => {
    const isInFlight = state === "submitting" || state === "polling";
    stopPolling();
    const requestId = requestIdRef.current;
    if (requestId && isInFlight) {
      cancelMutation({variables: {input: {requestId}}}).catch((e) => log.error("cancelSuggestAssignment error", e));
    }
    requestIdRef.current = null;
    jobIdRef.current = 0;
    setState("idle");
    setCandidates([]);
    setError(null);
    setValidationErrors([]);
  }, [stopPolling, cancelMutation, state]);

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  useEffect(() => {
    if (enabled) {
      void refreshUsage();
    }
  }, [enabled, refreshUsage]);

  const isActive = state === "complete" && candidates.length > 0;

  return {
    submit,
    cancel,
    confirmCandidate,
    refreshUsage,
    state,
    candidates,
    error,
    validationErrors,
    isLoading: state === "submitting" || state === "polling",
    isAssigning,
    isActive,
    usageRemaining,
    usageLimit
  };
};

export type UseSuggestedAssignmentReturn = ReturnType<typeof useSuggestedAssignment>;
